"use strict";

/**
 * DGF-3054 — wallet button appearance, applied through the SDK's own options.
 *
 * The buttons are rendered by the Adyen Web Components SDK, so CSS cannot reach inside them. What
 * CSS can and cannot do differs per wallet, which is the asymmetry that made the two render at
 * different widths:
 *
 *   Google Pay renders a <button> that sizes itself to its own content unless it is told to fill.
 *               `buttonSizeMode: 'fill'` is the only way to make it stretch to its container; CSS
 *               width on the container has no effect on the button's intrinsic sizing.
 *   Apple Pay   renders an <apple-pay-button> web component. It has NO buttonSizeMode equivalent —
 *               Apple's documented API for its geometry is the --apple-pay-button-* CSS custom
 *               properties, which _expressPayments.scss sets.
 *
 * So equal rendered width needs both halves: this module for Google Pay, the stylesheet for Apple
 * Pay, and a single-column grid so both get the same container width to fill.
 *
 * Shared by both orchestrators. The PDP previously applied none of this, which is why its buttons
 * came out narrower than add-to-bag and unequal to each other.
 */

/**
 * Per-wallet SDK button options. Every value here is a decision taken after live review — none of
 * them are defaults, and none should be changed without re-reading the notes below.
 *
 * buttonColor: white on both wallets ("white-outline" is Apple's outlined white).
 *
 * buttonType: PLAIN on both wallets — the mark only, no text. This is what makes side-by-side
 *   possible: the "Buy with G Pay" / "Buy with Apple Pay" text variants carry a content minimum
 *   width that no Superdry surface can supply twice over (the mini bag popover is a fixed 375px,
 *   giving about 169px per button), which is what pushed Google Pay outside the mini bag panel on
 *   Dev. A mark-only button has no text to fit, so it sits comfortably in that width.
 *
 *   For Google Pay this OVERRIDES the vendor's hardcoded buttonType: 'buy' (googlepay.js:343). That
 *   works because getConfig is wrapped on the instance and the overrides are merged last, so this
 *   value wins — no vendor file is modified. For Apple Pay the vendor sets no buttonType, so this
 *   is simply added.
 *
 *   Changing either back to a text variant REQUIRES revisiting $express-button-min-width in
 *   components/_expressPayments.scss. The two are coupled: a text variant in a 169px box overflows.
 *   The specs fail if the variant changes, on purpose.
 *
 * buttonSizeMode: Google Pay only — Apple Pay has no equivalent, see the note above. Note it also
 *   makes Google scale its content to the box it is given, so the container must be bounded in BOTH
 *   dimensions; the stylesheet gives the items a definite height for that reason, and mark-only is
 *   not a substitute for it.
 */
/**
 * Corner radius, in CSS pixels. Square, on the user's direction after seeing 3px on Dev.
 *
 * Deliberately NOT $border-radius. That variable is 0.1875rem — 3px — and is what `.add-to-cart`
 * inherits through Bootstrap's $btn-border-radius, so the original request to "match add-to-bag"
 * resolved to 3px and was implemented that way first. Seen live, 3px still read as rounded, and square
 * was preferred. The wallets are therefore intentionally squarer than every other button on the site.
 * Do not "correct" this back to $border-radius to make it match `.btn` again.
 *
 * Google renders its own button and takes the radius from this SDK option — CSS on the element cannot
 * reach it, which is why Google Pay came out rounder than add-to-bag until this was set. Apple Pay
 * takes its radius from --apple-pay-button-border-radius in components/_expressPayments.scss, which is
 * set to the same value there; both are needed, and they must be changed together.
 *
 * DUPLICATED VALUE, unavoidably: this is client JavaScript and cannot read a SCSS variable.
 */
const BUTTON_RADIUS_PX = 0;

/**
 * Mark only, on both wallets.
 *
 * The "buy" text variant — "Buy with  Pay" and "Buy with G Pay" — was tried on Dev and reversed on
 * the user's direction. Do not reinstate it without asking; it has now been rejected once.
 *
 * Two things worth keeping from that attempt. First, the wording is NOT ours to choose: both vendors
 * render a fixed set of strings and neither offers "Pay now with", so "buy" was the only value giving
 * both buttons matching phrasing. Second, the text variant is only physically possible while the
 * wallets are STACKED — side by side, ~169px each in the 375px mini bag cannot fit the text, which is
 * what pushed Google Pay outside the panel earlier. So a text variant additionally requires the
 * stacked layout and a raised $express-button-min-width; mark-only has neither constraint.
 */
const BUTTON_TYPE = "plain";

const BUTTON_OPTIONS = {
    applepay: { buttonColor: "white-outline", buttonType: BUTTON_TYPE },
    googlepay: {
        buttonColor: "white",
        buttonSizeMode: "fill",
        buttonType: BUTTON_TYPE,
        buttonRadius: BUTTON_RADIUS_PX
    }
};

/**
 * DGF-4651 — Apple's 'name' is a separate contact-field key that 'postalAddress' does NOT include,
 * and the sheet returns only what was requested — so the name was never asked for and orders
 * reached SOM without a surname. Added via the getConfig wrapper (app_adyen_SFRA untouched);
 * Google Pay already requests format:'FULL'.
 */
const APPLE_PAY_NAME_FIELD = "name";

/**
 * Unions 'name' into the vendor's contact-fields array without discarding future vendor additions.
 */
function withNameField(existingFields) {
    const fields = Array.isArray(existingFields) ? existingFields.slice() : [];
    if (fields.indexOf(APPLE_PAY_NAME_FIELD) === -1) {
        fields.push(APPLE_PAY_NAME_FIELD);
    }
    return fields;
}

/**
 * Wraps a wallet instance's getConfig so Superdry's button options are merged into whatever the
 * vendor builds.
 *
 * Wrapped on the instance rather than subclassed because getConfig is an instance arrow property on
 * GooglePay (googlepay.js:340) and a prototype method on ApplePay (applepay.js:283); an instance
 * wrapper covers both, and neither vendor class is modified.
 *
 * @param {Object} wallet - vendor wallet instance
 * @param {string} walletType - "applepay" or "googlepay"
 * @returns {Object} the same wallet instance
 */
function applyButtonStyling(wallet, walletType) {
    if (!wallet || typeof wallet.getConfig !== "function") {
        return wallet;
    }
    const overrides = BUTTON_OPTIONS[walletType];
    if (!overrides) {
        return wallet;
    }
    const original = wallet.getConfig.bind(wallet);
    wallet.getConfig = function getConfigWithSuperdryStyling() {
        const vendorConfig = original();
        const merged = Object.assign({}, vendorConfig, overrides);
        if (walletType === "applepay") {
            // makes Apple collect and return the shopper name - see APPLE_PAY_NAME_FIELD
            merged.requiredShippingContactFields = withNameField(vendorConfig.requiredShippingContactFields);
            merged.requiredBillingContactFields = withNameField(vendorConfig.requiredBillingContactFields);
        }
        return merged;
    };
    return wallet;
}

module.exports = {
    BUTTON_OPTIONS: BUTTON_OPTIONS,
    BUTTON_RADIUS_PX: BUTTON_RADIUS_PX,
    applyButtonStyling: applyButtonStyling
};
